import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  FileText, CheckCircle, Loader2, Clock, Eye, Send, Download, PenLine,
} from 'lucide-react';

export default function FormsPage() {
  const { api } = useAuth();

  // --- Forms & Waivers state ---
  const [myForms, setMyForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [activeForm, setActiveForm] = useState(null);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // --- Agreements state ---
  const [signatures, setSignatures] = useState([]);
  const [signaturesLoading, setSignaturesLoading] = useState(true);

  useEffect(() => { fetchMyForms(); }, []);

  useEffect(() => {
    api.get('/documents/my-signatures/')
      .then(r => setSignatures(r.data))
      .catch(() => toast.error('Failed to load agreements'))
      .finally(() => setSignaturesLoading(false));
  }, [api]);

  const fetchMyForms = async () => {
    try {
      const r = await api.get('/forms/manage/my-forms/');
      setMyForms(r.data);
    } catch {
      try {
        const r = await api.get('/forms/manage/');
        const templates = r.data?.results || r.data || [];
        setMyForms(templates.map(t => ({
          assignment_id: null,
          template_id: t.id,
          template_name: t.name,
          form_type: t.form_type,
          description: t.description,
          fields: t.fields || [],
          required: t.required,
          learner_name: null,
          learner_id: null,
          status: 'pending',
        })));
      } catch {}
    } finally { setFormsLoading(false); }
  };

  const openForm = (form) => {
    setActiveForm(form);
    setResponses({});
    setFormOpen(true);
  };

  const updateResponse = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
  };

  const submitForm = async () => {
    const fields = activeForm.fields || [];
    for (const field of fields) {
      if (field.required && !responses[field.id || field.label]) {
        toast.error(`Please fill in: ${field.label}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await api.post('/forms/manage/submit/', {
        assignment_id: activeForm.assignment_id,
        template_id: activeForm.template_id,
        learner_id: activeForm.learner_id,
        responses,
      });
      toast.success('Form submitted successfully!');
      setFormOpen(false);
      fetchMyForms();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit form');
    } finally { setSubmitting(false); }
  };

  const downloadPdf = async (sigId, title, parentName, learnerName) => {
    try {
      const r = await api.get(`/documents/signatures/${sigId}/download/`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      const parts = [title, parentName, learnerName].filter(Boolean).join('_').replace(/\s+/g, '_');
      a.download = `${parts || 'signed-document'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const pendingForms = myForms.filter(f => f.status === 'pending');
  const completedForms = myForms.filter(f => f.status === 'completed');
  const pendingSignatures = signatures.filter(s => !s.completed_at);
  const completedSignatures = signatures.filter(s => !!s.completed_at);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <FileText className="w-6 h-6" /> Forms & Agreements
        </h1>
        <p className="text-muted-foreground">Complete required forms and sign agreement documents for your learners.</p>
      </div>

      <Tabs defaultValue="forms">
        <TabsList className="mb-4">
          <TabsTrigger value="forms">
            Forms & Waivers
            {pendingForms.length > 0 && (
              <Badge className="ml-2 bg-yellow-500 text-white text-[10px] px-1.5 py-0">{pendingForms.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="agreements">
            Agreements
            {pendingSignatures.length > 0 && (
              <Badge className="ml-2 bg-yellow-500 text-white text-[10px] px-1.5 py-0">{pendingSignatures.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ── Forms & Waivers ── */}
        <TabsContent value="forms" className="space-y-6">
          {formsLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : myForms.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-muted-foreground">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-lg font-semibold">No forms assigned</p>
              <p className="text-sm">You're all caught up! New forms will appear here when assigned.</p>
            </CardContent></Card>
          ) : (
            <>
              {pendingForms.length > 0 && (
                <div>
                  <h2 className="font-semibold text-base mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-yellow-600" /> Pending ({pendingForms.length})
                  </h2>
                  <div className="space-y-3">
                    {pendingForms.map((form, i) => (
                      <Card key={i} className="border-l-4 border-l-yellow-400">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{form.template_name}</h3>
                              {form.required && <Badge className="bg-red-100 text-red-700 text-xs">Required</Badge>}
                              <Badge variant="outline" className="text-xs">{form.form_type}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{form.description}</p>
                            {form.learner_name && <p className="text-xs text-blue-600 mt-1">For: {form.learner_name}</p>}
                          </div>
                          <Button onClick={() => openForm(form)} className="bg-blue-600 hover:bg-blue-700 shrink-0">
                            <Eye className="h-4 w-4 mr-2" /> View & Sign
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {completedForms.length > 0 && (
                <div>
                  <h2 className="font-semibold text-base mb-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" /> Completed ({completedForms.length})
                  </h2>
                  <div className="space-y-2">
                    {completedForms.map((form, i) => (
                      <Card key={i} className="border-l-4 border-l-green-400 opacity-80">
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-600" />
                              <h3 className="font-medium">{form.template_name}</h3>
                              <Badge variant="outline" className="text-xs">{form.form_type}</Badge>
                            </div>
                            {form.learner_name && <p className="text-xs text-muted-foreground ml-6">For: {form.learner_name}</p>}
                          </div>
                          <Badge className="bg-green-100 text-green-700">Signed</Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* ── Agreements ── */}
        <TabsContent value="agreements" className="space-y-6">
          {signaturesLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin" /></div>
          ) : signatures.length === 0 ? (
            <Card><CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mb-4 opacity-30" />
              <h3 className="font-semibold text-lg mb-1">No agreements yet</h3>
              <p className="text-muted-foreground text-sm">When a session requires an agreement, it will appear here for you to sign.</p>
            </CardContent></Card>
          ) : (
            <>
              {pendingSignatures.length > 0 && (
                <div>
                  <h2 className="font-semibold text-base mb-3 flex items-center gap-2">
                    <Clock className="h-4 w-4 text-yellow-600" /> Needs Signature ({pendingSignatures.length})
                  </h2>
                  <div className="space-y-3">
                    {pendingSignatures.map(sig => (
                      <Card key={sig.id} className="border-l-4 border-l-yellow-400">
                        <CardContent className="flex items-center gap-4 py-4">
                          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                            <Clock className="h-5 w-5 text-amber-500" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{sig.document_title}</p>
                            {sig.learner_name && <p className="text-sm text-muted-foreground">For: {sig.learner_name}</p>}
                          </div>
                          <Link to={`/parent/documents/${sig.document}/sign${sig.learner ? `?learner=${sig.learner}` : ''}`}>
                            <Button size="sm">
                              <PenLine className="h-3.5 w-3.5 mr-1" /> Sign
                            </Button>
                          </Link>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {completedSignatures.length > 0 && (
                <div>
                  <h2 className="font-semibold text-base mb-3 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" /> Signed ({completedSignatures.length})
                  </h2>
                  <div className="space-y-2">
                    {completedSignatures.map(sig => (
                      <Card key={sig.id} className="border-l-4 border-l-green-400 opacity-80">
                        <CardContent className="flex items-center gap-4 py-4">
                          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium">{sig.document_title}</p>
                            {sig.learner_name && <p className="text-sm text-muted-foreground">For: {sig.learner_name}</p>}
                            {sig.completed_at && (
                              <p className="text-xs text-muted-foreground">Signed {new Date(sig.completed_at).toLocaleDateString()}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Badge className="bg-green-100 text-green-700">Signed</Badge>
                            {sig.signed_pdf_url && (
                              <Button size="sm" variant="outline" onClick={() => downloadPdf(sig.id, sig.document_title, sig.parent_print_name, sig.learner_name)}>
                                <Download className="h-3.5 w-3.5 mr-1" /> PDF
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Form fill/sign dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {activeForm && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" /> {activeForm.template_name}
                </DialogTitle>
                {activeForm.learner_name && <p className="text-sm text-blue-600">For: {activeForm.learner_name}</p>}
              </DialogHeader>

              {activeForm.description && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  {activeForm.description}
                </div>
              )}

              <div className="space-y-4 py-2">
                {(activeForm.fields || []).length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground">
                    <p>This form has no fields to fill out.</p>
                    <p className="text-sm">Click "Sign & Submit" to acknowledge and complete this form.</p>
                  </div>
                ) : (
                  (activeForm.fields || []).map((field, i) => {
                    const fieldId = field.id || field.label;
                    return (
                      <div key={i} className="space-y-1.5">
                        <Label className="flex items-center gap-1">
                          {field.label}
                          {field.required && <span className="text-red-500">*</span>}
                        </Label>
                        {field.type === 'text' && <Input value={responses[fieldId] || ''} onChange={e => updateResponse(fieldId, e.target.value)} placeholder={field.placeholder || ''} />}
                        {field.type === 'email' && <Input type="email" value={responses[fieldId] || ''} onChange={e => updateResponse(fieldId, e.target.value)} placeholder={field.placeholder || 'email@example.com'} />}
                        {field.type === 'phone' && <Input type="tel" value={responses[fieldId] || ''} onChange={e => updateResponse(fieldId, e.target.value)} placeholder={field.placeholder || '(555) 123-4567'} />}
                        {field.type === 'number' && <Input type="number" value={responses[fieldId] || ''} onChange={e => updateResponse(fieldId, e.target.value)} />}
                        {field.type === 'textarea' && <Textarea value={responses[fieldId] || ''} onChange={e => updateResponse(fieldId, e.target.value)} placeholder={field.placeholder || ''} rows={3} />}
                        {field.type === 'date' && <Input type="date" value={responses[fieldId] || ''} onChange={e => updateResponse(fieldId, e.target.value)} />}
                        {field.type === 'checkbox' && (
                          <div className="flex items-center gap-2">
                            <input type="checkbox" checked={responses[fieldId] === true} onChange={e => updateResponse(fieldId, e.target.checked)} className="rounded" />
                            <span className="text-sm">{field.placeholder || 'I agree'}</span>
                          </div>
                        )}
                        {field.type === 'select' && (
                          <Select value={responses[fieldId] || ''} onValueChange={v => updateResponse(fieldId, v)}>
                            <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                            <SelectContent>
                              {(field.options || []).map((opt, j) => <SelectItem key={j} value={opt}>{opt}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })
                )}

                <div className="border-t pt-4 mt-4">
                  <div className="flex items-start gap-2">
                    <input type="checkbox" checked={responses['_acknowledged'] === true} onChange={e => updateResponse('_acknowledged', e.target.checked)} className="rounded mt-1" />
                    <p className="text-sm">I acknowledge that I have read and understand the contents of this form. I agree to the terms stated above.</p>
                  </div>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
                <Button onClick={submitForm} disabled={submitting || !responses['_acknowledged']} className="bg-green-600 hover:bg-green-700">
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Sign & Submit
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
